// 모니터링(체크리스트 정량지표 연동) API.
// 모든 호출은 apiFetch<T>로 JWT·refresh 횡단 관심사를 재사용한다.

import { apiFetch, apiRequest } from '~/api/client'
import { readDelimitedStream, sseData } from '~/lib/stream'
import type {
    AssistCitation,
    AssistResponse,
    AutoMapResponse,
    Board,
    BoardAIResponse,
    BoardConfig,
    BoardQueryResult,
    BoardVersionItem,
    DeviationDetail,
    ExtractedRow,
    ExtractImageResponse,
    IngestBatch,
    IngestToken,
    MonitoringAlert,
    MonitoringDashboard,
    MonitoringIndicator,
    MonitoringReading,
    OverviewResponse,
    ApiProfileResponse,
    ReadingsPage,
    StatWindowKind,
} from '~/api/types'

export function updateNotifySettings(
    id: string,
    body: {
        notifyEmail: string | null
        notifyEmailOn: boolean
    }
): Promise<MonitoringDashboard> {
    return apiFetch<MonitoringDashboard>(
        `/monitoring/dashboards/${id}/notify-settings`,
        { method: 'PUT', body }
    )
}

export function listDashboards(): Promise<MonitoringDashboard[]> {
    return apiFetch<MonitoringDashboard[]>('/monitoring/dashboards')
}

export function createDashboard(body: {
    name: string
    assessmentId?: string | null
}): Promise<MonitoringDashboard> {
    return apiFetch<MonitoringDashboard>('/monitoring/dashboards', {
        method: 'POST',
        body,
    })
}

export function getDashboard(id: string): Promise<MonitoringDashboard> {
    return apiFetch<MonitoringDashboard>(`/monitoring/dashboards/${id}`)
}

/** 대시보드에 연결할 평가를 지정/해제한다(null이면 해제). */
export function linkDashboardAssessment(
    id: string,
    assessmentId: string | null
): Promise<MonitoringDashboard> {
    return apiFetch<MonitoringDashboard>(
        `/monitoring/dashboards/${id}/assessment`,
        { method: 'PUT', body: { assessmentId } }
    )
}

/** 대시보드 삭제. 측정값·매핑·알림도 함께 제거된다. */
export function deleteDashboard(id: string): Promise<void> {
    return apiFetch<void>(`/monitoring/dashboards/${id}`, { method: 'DELETE' })
}

export function listIndicators(id: string): Promise<MonitoringIndicator[]> {
    return apiFetch<MonitoringIndicator[]>(
        `/monitoring/dashboards/${id}/indicators`
    )
}

export function getMapping(
    id: string,
    sourceType: string
): Promise<{
    sourceType: string
    mapping: Record<string, string>
    measuredAtKey?: string | null
    recordsPath?: string | null
}> {
    return apiFetch(
        `/monitoring/dashboards/${id}/mapping?source_type=${encodeURIComponent(sourceType)}`
    )
}

export function saveMapping(
    id: string,
    body: {
        sourceType: string
        mapping: Record<string, string>
        measuredAtKey?: string | null
        recordsPath?: string | null
    }
): Promise<{ sourceType: string; mapping: Record<string, string> }> {
    return apiFetch(`/monitoring/dashboards/${id}/mapping`, {
        method: 'PUT',
        body,
    })
}

/** API 연동 적재 토큰을 (재)발급한다. IoT/외부 시스템이 X-Ingest-Token으로 적재 시 사용. */
export function issueIngestToken(id: string): Promise<IngestToken> {
    return apiFetch<IngestToken>(`/monitoring/dashboards/${id}/ingest-token`, {
        method: 'POST',
    })
}

export interface IngestByTokenResult {
    ingested: number
    dashboardId: string
}

/** 발급 토큰으로 임의 구조 JSON을 무인증 적재 엔드포인트에 보낸다(브라우저 테스트 전송용). */
export function ingestByToken(
    token: string,
    payload: unknown
): Promise<IngestByTokenResult> {
    return apiFetch<IngestByTokenResult>('/monitoring/ingest', {
        method: 'POST',
        headers: { 'X-Ingest-Token': token },
        body: payload,
        skipAuth: true, // 무인증(토큰 기반) 엔드포인트
    })
}

/** 장비가 보낼 샘플 JSON 1건을 프로파일링하고 AI 매핑을 제안받는다(적재 전 미리 매핑). */
export function profileApiSample(
    id: string,
    sample: unknown,
    recordsPath?: string | null
): Promise<ApiProfileResponse> {
    return apiFetch<ApiProfileResponse>(
        `/monitoring/dashboards/${id}/api/profile`,
        { method: 'POST', body: { sample, recordsPath } }
    )
}

export function autoMap(
    id: string,
    body: { sourceType: string; sample: Array<Record<string, unknown>> }
): Promise<AutoMapResponse> {
    return apiFetch<AutoMapResponse>(`/monitoring/dashboards/${id}/map/auto`, {
        method: 'POST',
        body,
    })
}

export function ingestReadings(
    id: string,
    body: {
        sourceType: string
        records: Array<Record<string, unknown>>
        measuredAtKey?: string | null
        source?: string
        filename?: string | null
    }
): Promise<{ inserted: number }> {
    return apiFetch(`/monitoring/dashboards/${id}/readings`, {
        method: 'POST',
        body,
    })
}

/** 적재 이력 — CSV·이미지 업로드 배치 목록(최신순). */
export function listIngestBatches(id: string): Promise<IngestBatch[]> {
    return apiFetch<IngestBatch[]>(
        `/monitoring/dashboards/${id}/ingest-batches`
    )
}

/** 적재 배치 삭제 — 이 업로드로 적재된 측정값도 함께 제거된다(cascade). */
export function deleteIngestBatch(id: string, batchId: string): Promise<void> {
    return apiFetch<void>(
        `/monitoring/dashboards/${id}/ingest-batches/${batchId}`,
        { method: 'DELETE' }
    )
}

export function listReadings(
    id: string,
    opts: {
        offset?: number
        limit?: number
        fieldId?: string
        dateFrom?: string
        dateTo?: string
        q?: string
        batchId?: string
    } = {}
): Promise<ReadingsPage> {
    const params = new URLSearchParams()
    if (opts.offset != null) params.set('offset', String(opts.offset))
    if (opts.limit != null) params.set('limit', String(opts.limit))
    if (opts.fieldId) params.set('field_id', opts.fieldId)
    if (opts.dateFrom) params.set('date_from', opts.dateFrom)
    if (opts.dateTo) params.set('date_to', opts.dateTo)
    if (opts.q) params.set('q', opts.q)
    if (opts.batchId) params.set('batch_id', opts.batchId)
    const suffix = params.size > 0 ? `?${params.toString()}` : ''
    return apiFetch<ReadingsPage>(
        `/monitoring/dashboards/${id}/readings${suffix}`
    )
}

export function updateReading(
    id: string,
    readingId: string,
    body: { value: string; unit?: string | null }
): Promise<MonitoringReading> {
    return apiFetch<MonitoringReading>(
        `/monitoring/dashboards/${id}/readings/${readingId}`,
        { method: 'PATCH', body }
    )
}

export function deleteReading(id: string, readingId: string): Promise<void> {
    return apiFetch<void>(
        `/monitoring/dashboards/${id}/readings/${readingId}`,
        { method: 'DELETE' }
    )
}

/** 수동 측정값 1건 추가(인라인 행 추가용). */
export function addManualReading(
    id: string,
    body: {
        fieldId: string
        value: string
        unit?: string | null
        measuredAt?: string | null
    }
): Promise<MonitoringReading> {
    return apiFetch<MonitoringReading>(
        `/monitoring/dashboards/${id}/readings/manual`,
        { method: 'POST', body }
    )
}

/** 점검 현황 — 지표별 최신 측정값 ↔ 기준 대조 결과 + KPI. */
export function getOverview(id: string): Promise<OverviewResponse> {
    return apiFetch<OverviewResponse>(`/monitoring/dashboards/${id}/overview`)
}

/** 기준 이탈 점검 — 지표 드릴다운(시계열·위험 평가항목·기준 해설). all=true면 전 정량지표. */
export function getDeviations(
    id: string,
    all = false
): Promise<DeviationDetail[]> {
    const suffix = all ? '?all_indicators=true' : ''
    return apiFetch<DeviationDetail[]>(
        `/monitoring/dashboards/${id}/deviations${suffix}`
    )
}

export function listAlerts(id: string): Promise<MonitoringAlert[]> {
    return apiFetch<MonitoringAlert[]>(`/monitoring/dashboards/${id}/alerts`)
}

export function updateAlert(
    id: string,
    alertId: string,
    status: string
): Promise<MonitoringAlert> {
    return apiFetch<MonitoringAlert>(
        `/monitoring/dashboards/${id}/alerts/${alertId}`,
        { method: 'PATCH', body: { status } }
    )
}

/** 모니터링 AI 어시스턴트 — 이탈 현황 + HACCP 근거로 답하고 클릭형 선택지 제안. */
export function assist(
    id: string,
    body: {
        question: string
        history: Array<{ role: string; content: string }>
    }
): Promise<AssistResponse> {
    return apiFetch<AssistResponse>(`/monitoring/dashboards/${id}/assist`, {
        method: 'POST',
        body,
    })
}

/** AI 어시스턴트 SSE — 답변 토큰을 실시간으로 흘리고 마지막에 선택지·근거를 전달한다. */
export async function assistStream(
    id: string,
    body: {
        question: string
        history: Array<{ role: string; content: string }>
    },
    on: {
        token: (text: string) => void
        done: (data: { choices: string[]; citations: AssistCitation[] }) => void
    },
    signal?: AbortSignal
): Promise<void> {
    const res = await apiRequest(`/monitoring/dashboards/${id}/assist/stream`, {
        method: 'POST',
        body,
        headers: { Accept: 'text/event-stream' },
        signal,
    })
    if (!res.ok || !res.body) throw new Error('assist stream failed')
    for await (const line of readDelimitedStream(res.body)) {
        const json = sseData(line)
        if (!json) continue
        try {
            const ev = JSON.parse(json) as {
                type: string
                text?: string
                choices?: string[]
                citations?: AssistCitation[]
            }
            if (ev.type === 'token') on.token(ev.text ?? '')
            else if (ev.type === 'done') {
                on.done({
                    choices: ev.choices ?? [],
                    citations: ev.citations ?? [],
                })
            }
        } catch {
            // 깨진 라인은 건너뛴다(부분 청크는 이미 프레이밍 단계에서 합쳐진다).
        }
    }
}

export function getBoard(id: string): Promise<Board> {
    return apiFetch<Board>(`/monitoring/dashboards/${id}/board`)
}

export function saveBoard(
    id: string,
    body: { config: BoardConfig; note?: string | null }
): Promise<Board> {
    return apiFetch<Board>(`/monitoring/dashboards/${id}/board`, {
        method: 'PUT',
        body,
    })
}

export function listBoardVersions(id: string): Promise<BoardVersionItem[]> {
    return apiFetch<BoardVersionItem[]>(
        `/monitoring/dashboards/${id}/board/versions`
    )
}

export function rollbackBoard(id: string, version: number): Promise<Board> {
    return apiFetch<Board>(
        `/monitoring/dashboards/${id}/board/rollback/${version}`,
        { method: 'POST' }
    )
}

/** 분석보드 AI 빌더 — 자연어로 보드 생성/수정 또는 집계 질의(Text2SQL 대체). */
export function boardAI(
    id: string,
    body: { message: string; config: BoardConfig }
): Promise<BoardAIResponse> {
    return apiFetch<BoardAIResponse>(`/monitoring/dashboards/${id}/board/ai`, {
        method: 'POST',
        body,
    })
}

export function boardQuery(
    id: string,
    fieldId: string,
    opts: {
        dateFrom?: string
        dateTo?: string
        windowKind?: StatWindowKind
        windowN?: number
    } = {}
): Promise<BoardQueryResult> {
    const params = new URLSearchParams({ field_id: fieldId })
    if (opts.dateFrom) params.set('date_from', opts.dateFrom)
    if (opts.dateTo) params.set('date_to', opts.dateTo)
    if (opts.windowKind) params.set('window_kind', opts.windowKind)
    if (opts.windowN != null) params.set('window_n', String(opts.windowN))
    return apiFetch<BoardQueryResult>(
        `/monitoring/dashboards/${id}/board/query?${params.toString()}`
    )
}

/** CCP 일지 이미지(VLM) → 일자별 측정값 추출(미리보기, 적재 전). */
export function extractImage(
    id: string,
    body: { imageBase64: string; mime: string }
): Promise<ExtractImageResponse> {
    return apiFetch<ExtractImageResponse>(
        `/monitoring/dashboards/${id}/extract-image`,
        { method: 'POST', body }
    )
}

/** 추출·확인된 일자별 측정값을 적재한다. */
export function ingestExtracted(
    id: string,
    body: { rows: ExtractedRow[]; filename?: string | null }
): Promise<{ inserted: number }> {
    return apiFetch(`/monitoring/dashboards/${id}/readings/extracted`, {
        method: 'POST',
        body,
    })
}
